import { useState, useCallback } from 'react';
import { type LoginFormData, type LoginFormErrors, type LoginCredentials } from '../../../types/auth';
import { AuthValidationService } from '../services';

export interface UseLoginFormProps {
  onSubmit: (credentials: LoginCredentials) => Promise<void>;
  initialData?: Partial<LoginFormData>;
}

export interface UseLoginFormReturn {
  formData: LoginFormData;
  errors: LoginFormErrors;
  isValid: boolean;
  isDirty: boolean;
  isSubmitting: boolean;
  handleFieldChange: (field: keyof LoginFormData, value: string | boolean) => void;
  handleSubmit: () => Promise<void>;
  resetForm: () => void;
  validateForm: () => boolean;
  clearErrors: () => void;
}

const initialFormState: LoginFormData = {
  username: '',
  password: '',
  rememberMe: false,
};

/**
 * Custom hook for managing login form state and validation
 * 
 * Encapsulates all login form logic following clean architecture principles:
 * - Form state management
 * - Real-time validation
 * - Submission handling
 * - Error management
 */
export const useLoginForm = ({
  onSubmit,
  initialData = {},
}: UseLoginFormProps): UseLoginFormReturn => {
  const [formData, setFormData] = useState<LoginFormData>({
    ...initialFormState,
    ...initialData,
  });
  
  const [errors, setErrors] = useState<LoginFormErrors>({});
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Validates the entire form and returns validation status
   */
  const validateForm = useCallback((): boolean => {
    const validationErrors = AuthValidationService.validateLoginForm(formData);
    setErrors(validationErrors);
    return AuthValidationService.isFormValid(validationErrors);
  }, [formData]);

  /**
   * Handles field value changes with real-time validation
   */
  const handleFieldChange = useCallback((field: keyof LoginFormData, value: string | boolean) => {
    setFormData(current => ({
      ...current,
      [field]: value,
    }));

    // Mark form as dirty
    if (!isDirty) {
      setIsDirty(true);
    }

    // Clear field error if it exists and perform real-time validation
    if (typeof value === 'string' && field !== 'rememberMe') {
      const fieldError = AuthValidationService.validateField(field, value);
      setErrors(currentErrors => {
        const newErrors = { ...currentErrors };
        if (fieldError) {
          newErrors[field] = fieldError;
        } else {
          delete newErrors[field];
        }
        return newErrors;
      });
    }
  }, [isDirty]);

  /**
   * Handles form submission with validation and error handling
   */
  /**
   * Resets form to initial state
   */
  const resetForm = useCallback(() => {
    setFormData({ ...initialFormState, ...initialData });
    setErrors({});
    setIsDirty(false);
    setIsSubmitting(false);
  }, [initialData]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Validate form before submission
      const isFormValid = validateForm();
      
      if (!isFormValid) {
        return;
      }

      // Convert form data to credentials
      const credentials: LoginCredentials = {
        username: formData.username.trim(),
        password: formData.password,
        rememberMe: formData.rememberMe,
      };

      // Submit credentials
      await onSubmit(credentials);

      // Reset form on successful submission
      resetForm();
    } catch (error) {
      // Error handling is done at the component level
      // This allows for different error handling strategies
      console.error('Login form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, isSubmitting, validateForm, onSubmit, resetForm]);

  /**
   * Clears all form errors
   */
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  // Calculate if form is valid
  const isValid = AuthValidationService.isFormValid(errors) && 
                  formData.username.trim() !== '' && 
                  formData.password !== '';

  return {
    formData,
    errors,
    isValid,
    isDirty,
    isSubmitting,
    handleFieldChange,
    handleSubmit,
    resetForm,
    validateForm,
    clearErrors,
  };
};